import { useRef } from 'react';
import { Scale } from 'lucide-react';
import { weightTrend } from '@/engine/trend';
import { useProfileState } from '@/features/profile/useProfile';
import { displayToKg, kgToDisplay, num } from '@/lib/format';
import { cn } from '@/lib/utils';
import { inputClass } from '@/components/ui/Field';
import { setBodyWeight, useBodyWeights, useTodayWeight } from './useWorkout';

/**
 * Registro de peso corporal con su tendencia.
 *
 * Se enseñan los dos números a propósito: el de hoy y el suavizado. Mirar solo
 * la báscula lleva a cambiar la dieta por dos kilos de agua; mirar solo la
 * tendencia esconde el dato que acabas de medir.
 */
export function BodyWeightCard({ date }: { date: string }) {
  const profile = useProfileState();
  const stored = useTodayWeight(date);
  const history = useBodyWeights();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const unit = profile.status === 'listo' ? profile.profile.displayUnit : 'kg';
  const shown = stored == null ? '' : String(round1(kgToDisplay(stored, unit)));

  const trend = weightTrend(history);
  const latest = trend.at(-1);

  function save() {
    const raw = inputRef.current?.value ?? '';
    const value = Number(raw.replace(',', '.'));
    if (!(value > 0)) return;
    void setBodyWeight(date, displayToKg(value, unit));
  }

  return (
    <section
      aria-labelledby="peso"
      className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3"
    >
      <div className="flex items-center gap-2">
        <Scale aria-hidden="true" className="size-5 text-[var(--color-text-muted)]" />
        <h2 id="peso" className="flex-1 text-sm font-semibold">
          Peso de hoy
        </h2>
        {latest && history.length > 1 ? (
          <p className="text-xs text-[var(--color-text-muted)] tabular">
            tendencia {num(round1(kgToDisplay(latest.trend, unit)), 1)} {unit}
          </p>
        ) : null}
      </div>

      <div className="flex gap-2">
        {/* Input NO controlado, con `key` atada al valor guardado: así se
            reinicia solo cuando llega el dato de IndexedDB, sin un efecto que
            haga setState y dispare renders en cascada mientras se escribe. */}
        <input
          key={shown}
          ref={inputRef}
          type="number"
          inputMode="decimal"
          step="0.1"
          min={20}
          max={400}
          defaultValue={shown}
          aria-label={`Peso de hoy en ${unit}`}
          placeholder={unit === 'kg' ? '78,4' : '172'}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              save();
              e.currentTarget.blur();
            }
          }}
          className={cn(inputClass, 'flex-1 text-right tabular')}
        />
        <button
          type="button"
          onClick={save}
          className="tap rounded-xl bg-[var(--color-brand-button)] px-4 text-sm font-semibold text-[var(--color-on-brand)]"
        >
          Guardar
        </button>
      </div>
    </section>
  );
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
